import { isJsonString } from '../index.js';
/**
 * Gets the parent page dataLayer window object or returns undefined
 * @param window
 * @returns
 */
export const getParentPageDataLayerWindow = (window) => {
    try {
        const { dataLayer } = window.top;
        if (dataLayer) {
            const parentWindowObj = dataLayer.find((windowObj) => windowObj.event === 'legacyPageview') ||
                dataLayer.find((windowObj) => windowObj.event === 'unifiedPageview');
            return parentWindowObj;
        }
    }
    catch (error) {
        return undefined;
    }
};
/**
 * Retrieves the parent page documentId from the dataLayer
 *
 * When dataLayer.event = 'unifiedPageView' - key name is documentId
 *
 * When dataLayer.event = 'legacyPageView' - key name is karmaId
 *
 * @returns {string} Returns the parent page documentId value if found, or an 0 as a string if not found.
 *
 * DO NOT UPDATE THIS TO RETURN AN EMPTY STRING - SPLA requires a documentId value in the api fetch payload.
 */
export const getDocumentId = () => {
    const sm_params = window.sm_params ? window.sm_params : undefined;
    if (sm_params && sm_params.docId) {
        return sm_params.docId;
    }
    const parentWindowObj = getParentPageDataLayerWindow(window);
    if (parentWindowObj && parentWindowObj.karmaId) {
        return parentWindowObj.karmaId;
    }
    if (parentWindowObj && parentWindowObj.documentId) {
        return parentWindowObj.documentId;
    }
    // One last check since we can't find it on the parent page (maybe requestId was passed in a query string)
    const params = new URLSearchParams(window.location?.search);
    return params.get('documentId') ?? '0';
};
/**
 * Retreives MUID - Meredith User ID - values for telemetry
 *
 * This will live on the iFrame window if ad is presented through GAM
 *
 * Otherwise, it will be found on parent page (same value gam would use)
 *
 * When dataLayer.event = 'unifiedPageView' - key name is muid (all lowercase)
 *
 * When dataLayer.event = 'legacyPageView' - key name is MUID (all uppercase)
 *
 * @returns {string} Returns the MUID value if found, or an empty string if not found.
 */
export const getMuid = (window) => {
    //Assuming ad unit is surfaced through GAM first
    const sm_params = window.sm_params ? window.sm_params : undefined;
    //Otherwise look at parent page dataLayer window
    const parentWindowObj = getParentPageDataLayerWindow(window);
    if (sm_params && sm_params.ka_muid) {
        return sm_params.ka_muid;
    }
    if (parentWindowObj && parentWindowObj.MUID) {
        return parentWindowObj.MUID;
    }
    if (parentWindowObj && parentWindowObj.muid) {
        return parentWindowObj.muid;
    }
    // One last check since we can't find it on the parent page (maybe muid was passed in a query string)
    const params = new URLSearchParams(window.location.search);
    return params.get('muid') ?? '';
};
/**
 * Retrieves the requestId
 *
 * This will live on the iFrame window if ad is presented through GAM
 *
 * Otherwise, it will be found on parent page (same value gam would use)
 *
 * When dataLayer.event = 'unifiedPageView' - key name is internalRequestid
 *
 * When dataLayer.event = 'legacyPageView' - key name is requestId
 *
 * @returns {string} Returns the requestId value if available, or an empty string if not found.
 */
export const getRequestId = (window) => {
    //Assuming ad unit is surfaced through GAM first
    const sm_params = window.sm_params;
    if (sm_params && sm_params.requestId) {
        return sm_params.requestId;
    }
    //Otherwise looking at parent page dataLayer
    const parentWindowObj = getParentPageDataLayerWindow(window);
    if (parentWindowObj && parentWindowObj.requestId) {
        return parentWindowObj.requestId;
    }
    if (parentWindowObj && parentWindowObj.internalRequestId) {
        return parentWindowObj.internalRequestId;
    }
    // One last check since we can't find it on the parent page (maybe requestId or REQUEST_ID was passed in a query string)
    const params = new URLSearchParams(window.location.search);
    return (params.get('requestId') || params.get('REQUEST_ID')) ?? '';
};
/**
 * Provides originReferer value for POLARIS TELEMETRY events
 *
 * 'originReferer' is the URL of the page on which the end-user has encountered the ad.
 *
 * @returns {string}
 */
export const getOriginReferer = () => {
    // If we are in an iframe, return 'origin' query string param (if present) or the referrer.
    // - Note: since we're in an iframe, we do not have access to our parent, usually (unless we're in a safe-frame). To get true page originReferrer, parent app must send this value into iframe via postMessage.
    if (window.top !== window.self)
        return new URLSearchParams(window.location.search).get('origin') || document.referrer;
    const regEx = /http:\/\/localhost:\d+[0-9]/;
    const originReferer = (new URLSearchParams(window.location.search).get('origin') ||
        window.parent.document.location.href).replace(regEx, 'https://www.allrecipes.com');
    return originReferer;
};
/**
 * Provides referer value for polaris telemetry events
 *
 * 'referer' is the URL of the page where the end-user came from (another webpage, Google, Facebook, etc.)
 *
 * If you're on localhost this will return an empty string.
 *
 * Reason: Referer URLs are passed between unrelated sites when navigation occurs due to a link click or JavaScript-based navigation. Referer URLs are NOT sent if the user uses the browser's chrome (e.g. address bar, back/forward buttons/etc) to navigate.
 * For security/privacy reasons, the Referer URL is stripped out when navigating from a HTTPS site to a HTTP site (e.g. from https://google.com to http://example.com).
 *
 * localhost uses http protocol so document.referrer will be empty. It should work on qa environment
 *
 * @returns {string}
 */
export const getReferer = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('referer') ?? document.referrer;
};
/**
 *
 * Retreives applicable Meredith Segment values for the end-user.
 *
 * This will live on the iFrame window if ad is presented through GAM
 *
 * Otherwise, it will be found on parent page (same value gam would use)
 *
 * Key will equal 'msg' or 'msg2'
 *
 * @returns {string}
 */
export const getMsg = (window, key) => {
    //Assuming ad unit is surfaced through GAM first
    const sm_params = window.sm_params ? window.sm_params : undefined;
    if (sm_params && sm_params.ka_msg) {
        return sm_params.ka_msg;
    }
    //Otherwise look at parent page window.top.Mntl.GPT.getAllSlots()[0]
    const _win = window.top !== null ? window.top : window;
    const segments = _win && _win.Mntl ? getMntlTargeting(key) : getKarmaTargeting(key);
    function getMntlTargeting(k) {
        return isJsonString(_win.localStorage[k])
            ? JSON.parse(_win.localStorage[k])
            : _win.localStorage[k];
    }
    function getKarmaTargeting(k) {
        if (_win && _win.karma && _win.karma.vars && k) {
            return isJsonString(_win.karma.vars[k])
                ? JSON.parse(_win.karma.vars[k])
                : _win.karma.vars[k];
        }
    }
    return segments?.length ? segments.join(',') : segments || '';
};
/**
 * Retrieves the sessionId from the dataLayer
 *
 * When dataLayer.event = 'unifiedPageView' - key name is internalSessionId
 *
 * Sam H confirmed not to worry about this for legacyPageView and continue to do what calvera did before
 *
 * when no internalSessionId found we will (continue to) use Date.now
 *
 * @returns {string} Returns the requestId value if available, or an empty string if not found.
 */
export const getSessionId = () => {
    const parentWindowObj = getParentPageDataLayerWindow(window);
    if (parentWindowObj && parentWindowObj.internalSessionId) {
        return parentWindowObj.internalSessionId;
    }
    // One last check since we can't find it on the parent page (maybe requestId was passed in a query string)
    const params = new URLSearchParams(window.location.search);
    return params.get('sessionId') ?? `${Date.now()}`;
};
/**
 * Retrieves the gamCreativeId
 *
 * This value exists ONLY when the ad unit is surfaced through GAM
 *
 * When the ad unit is surfaced through GAM it is presented in an iFrame.
 * The iframe window will have an sm_params object that can be accessed at this point to retrieve the value
 *
 * When viewing ad unit through localHost the index.html will contain a fake copy for testing purposes
 *
 * @param window - In this context it's the (iFrame) window in which the app was surfaced. NOT the parent page
 *
 * @returns {string} Returns the gamCreativeId value if available, or an empty string if not found.
 */
export const getGamCreativeId = (window) => {
    const sm_params = window.sm_params;
    if (sm_params && sm_params.creative_id) {
        return sm_params.creative_id;
    }
    if (sm_params && sm_params.gamCreativeId) {
        return sm_params.gamCreativeId;
    }
    // One last check since we can't gamCreativeId on the (iFrame) window (maybe gamCreativeId was passed in a query string)
    const params = new URLSearchParams(window.location.search);
    return params.get('gamCreativeId') ?? '';
};
/**
 * Retrieves the getGamLineItemId
 *
 * This value exists ONLY when the ad unit is surfaced through GAM
 *
 * When the ad unit is surfaced through GAM it is presented in an iFrame.
 * The iframe window will have an sm_params object that can be accessed at this point to retrieve the value
 *
 * When viewing ad unit through localHost the index.html will contain a fake copy for testing purposes
 *
 * @param window - In this context it's the (iFrame) window in which the app was surfaced. NOT the parent page
 *
 * @returns {string} Returns the getGamLineItemId value if available, or an empty string if not found.
 */
export const getGamLineItemId = (window) => {
    const sm_params = window.sm_params;
    if (sm_params && sm_params.line_item_id) {
        return sm_params.line_item_id;
    }
    if (sm_params && sm_params.gamLineItemId) {
        return sm_params.gamLineItemId;
    }
    // One last check since we can't gamLineItemId on the (iFrame) window (maybe gamLineItemId was passed in a query string)
    const params = new URLSearchParams(window.location.search);
    return params.get('gamLineItemId') ?? '';
};
/**
 * Retreives sTax Values for telemetry
 *
 * This will live on the iFrame window if ad is presented through GAM
 *
 * Otherwise, it will be found on parent page (same value gam would use)
 *
 * locahost has fake dummy values in index.html where as in prod we have a parent page.
 * This means that the only time the below variable is undefined is if we are previewing ad through console.
 * This is because there are no fake sm_params coming from BED and there is no parent page (ad viewed as stand alone application)
 */
export const getSTax = (window) => {
    console.log('Getting sTax values ... no try-catch');
    try {
        // An array to hold our string sTax values
        const sTaxValues = [];
        // Assuming ad unit is surfaced through GAM first
        const sm_params = window.sm_params ? window.sm_params : undefined;
        if (sm_params) {
            const taxKeyPropertyNames = Object.keys(sm_params).filter(key => key.includes('tax'));
            taxKeyPropertyNames.forEach(taxKeyPropertyName => sTaxValues.push(sm_params[taxKeyPropertyName]));
            return sTaxValues.join(',');
            ;
        }
        // Otherwise look at parent page window.top.Mntl.GPT.getAllSlots()[0]
        const _win = window.top !== null ? window.top : window;
        const mntlSlotsTargeting = _win && _win.Mntl
            ? _win.Mntl.GPT.getAllSlots()[0].targeting
            : sm_params;
        if (mntlSlotsTargeting && mntlSlotsTargeting.tax1) {
            sTaxValues.push(mntlSlotsTargeting.tax1);
        }
        if (mntlSlotsTargeting && mntlSlotsTargeting.tax2) {
            sTaxValues.push(mntlSlotsTargeting.tax2);
        }
        if (mntlSlotsTargeting && mntlSlotsTargeting.tax3) {
            sTaxValues.push(mntlSlotsTargeting.tax3);
        }
        return sTaxValues.join(',');
    }
    catch (error) {
        console.error('Error retrieving sTax values', error);
        return '';
    }
};
/**
 * The getPotentialGAMClickTracker function checks to see if a sm_params object exists on the parent window in which the application loaded.
 *
 * If it does not exist - return empty string
 *
 * If it does exist - check if a value for smcuu or smcue exists.
 * These in theory should be string values which are Google Ad Manager Click tracker redirect urls.
 * They are the same between smcuu or smcue except smcuu is unescaped the other is escaped.
 *
 * Google Ad Manager Click Tracker redirect urls prepend the actual (final) destination URL
 *
 * @returns (string)
 */
export const getPotentialGAMClickTracker = () => {
    if (window.sm_params) {
        const gamClickRedirectDestination = window.sm_params.smcuu ? window.sm_params.smcuu :
            window.sm_params.smcue ? window.sm_params.smcue :
                undefined;
        if (!gamClickRedirectDestination) {
            throw new Error(`Detected potential GAM setup but "sm_params.smcuu" or "sm_params.smcue" are empty`);
        }
        return gamClickRedirectDestination;
    }
    return '';
};
